import React, { useState, useCallback, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useError } from '../../hooks'
import { openResource } from '../../api'

import DataTable, { Row, RowData } from '../DataTable'
import Columns from '../Columns'
import Info from '../Info'
import DropdownMenu, { DropdownItem } from '../DropdownMenu'
import ContextMenu from '../ContextMenu'
import EditSiteDetails from '../EditSiteDetails'
import LocationRenderer from '../LocationRenderer'
import Spinner from '../Spinner'

import {
  doc,
  getDoc,
  deleteDoc,
  getDocs,
  collection,
  updateDoc,
  arrayRemove
} from 'firebase/firestore'
import { db } from '../../firebase'

function capitalize(s) {
  return `${s.slice(0, 1).toUpperCase()}${s.slice(1)}`
}

function SiteInfoList({ siteId, info, reloadSite }) {
  const setError = useError()
  const token = '' // replace with token if needed for `openResource`

  async function deleteSiteInfo(id) {
    try {
      if (window.confirm('Are you sure you wish to delete this item?')) {
        await deleteDoc(doc(db, 'sites', siteId, 'info', id))
        reloadSite()
      }
    } catch (err) {
      setError(err)
    }
  }

  return (
    <div>
      <DataTable
        columns={['Name', 'Type', 'Resource Type', 'Actions']}
        className='is-fullwidth is-hoverable'>
        {info.map((item) => (
          <Row key={item.id}>
            <RowData>{item.name}</RowData>
            <RowData>{capitalize(item.type)}</RowData>
            <RowData>{item.resource.mimeType}</RowData>
            <RowData>
              <DropdownMenu label='Actions' small={true}>
                <DropdownItem
                  onClick={() => openResource(item.resource.url, token)}>
                  View
                </DropdownItem>
                <DropdownItem onClick={() => deleteSiteInfo(item.id)}>
                  Delete
                </DropdownItem>
              </DropdownMenu>
            </RowData>
          </Row>
        ))}
      </DataTable>
    </div>
  )
}

function OrganizationBindingsList({
  siteId,
  organizationBindings,
  reloadSite
}) {
  const navigate = useNavigate()
  const setError = useError()

  const deleteOrganizationBinding = useCallback(
    async (organizationId) => {
      try {
        if (
          window.confirm(
            'Are you sure you wish to delete this organization binding?'
          )
        ) {
          const siteRef = doc(db, 'sites', siteId)
          await updateDoc(siteRef, {
            organization_bindings: arrayRemove(String(organizationId))
          })
          reloadSite()
        }
      } catch (err) {
        setError(err)
      }
    },
    [setError, reloadSite, siteId]
  )

  organizationBindings = organizationBindings.sort((a, b) =>
    String(a.id).localeCompare(String(b.id))
  )

  return (
    <div>
      <DataTable
        columns={['ID', 'Name', 'Actions']}
        className='is-fullwidth is-hoverable'>
        {organizationBindings
          .filter((x) => x)
          .map(({ id, name }) => (
            <Row key={id}>
              <RowData>{id}</RowData>
              <RowData onClick={() => navigate(`/organizations/${id}`)}>
                {name}
              </RowData>
              <RowData>
                <DropdownMenu label='Actions' small={true}>
                  <DropdownItem onClick={() => deleteOrganizationBinding(id)}>
                    Delete
                  </DropdownItem>
                </DropdownMenu>
              </RowData>
            </Row>
          ))}
      </DataTable>
    </div>
  )
}

export default function ({ id }) {
  const navigate = useNavigate()
  const setError = useError()
  const [editing, setEditing] = useState(false)
  const [site, setSite] = useState(null)

  const reloadSite = useCallback(async () => {
    try {
      const siteRef = doc(db, 'sites', id)
      const siteSnap = await getDoc(siteRef)

      if (!siteSnap.exists()) {
        throw new Error('Site not found')
      }

      const siteData = siteSnap.data()

      const infoSnap = await getDocs(collection(siteRef, 'info'))
      const info = infoSnap.docs.map((d) => ({ id: d.id, ...d.data() }))

      // const orgSnap = await getDocs(collection(siteRef, 'organization-bindings'))
      // const organizationBindings = orgSnap.docs.map((d) => ({
      //   id: d.id,
      //   organization: d.data().organization,
      // }))

      let organizationBindings = []

      if (Array.isArray(siteData.organization_bindings)) {
        const orgIds = siteData.organization_bindings

        const orgDocs = await Promise.all(
          orgIds.map(async (orgId) => {
            const docSnap = await getDoc(doc(db, 'organizations', orgId))
            return docSnap.exists() ? { id: orgId, ...docSnap.data() } : null
          })
        )

        organizationBindings = orgDocs.filter(Boolean) // remove nulls
      }

      setSite({
        id: siteSnap.id,
        ...siteData,
        info,
        organizationBindings
      })
    } catch (err) {
      setError(err)
    }
  }, [id, setError])

  useEffect(() => {
    reloadSite()
  }, [reloadSite])

  async function deleteSite() {
    try {
      if (window.confirm('Are you sure you wish to delete this site?')) {
        await deleteDoc(doc(db, 'sites', id))
        navigate('/sites/list')
      }
    } catch (err) {
      setError(err)
    }
  }

  if (!site) {
    return <Spinner />
  }

  return (
    <div>
      <ContextMenu
        renderTitle={() => site && <h1 className='title'>{site.name}</h1>}>
        <DropdownMenu label='Actions'>
          <DropdownItem
            onClick={() => navigate(`/sites/info/create?siteId=${site.id}`)}>
            Add new site information
          </DropdownItem>
          <DropdownItem
            onClick={() =>
              navigate(`/sites/organization-binding/create?siteId=${site.id}`)
            }>
            Add an organization binding
          </DropdownItem>
          <DropdownItem onClick={() => setEditing(true)}>
            Edit site details
          </DropdownItem>
          <DropdownItem onClick={deleteSite}>Delete site</DropdownItem>
        </DropdownMenu>
      </ContextMenu>
      <div className='box'>
        <h2 className='subtitle'>Site Details</h2>
        <Columns>
          <Info label='ID'>{site.id}</Info>
          <Info label='Name'>{site.name}</Info>
          <Info label='Organization'>
            <Link to={`/organizations/${site.organization.org_id}`}>
              {site.organization.org_name}
            </Link>
          </Info>
        </Columns>
        <Columns>
          {site.locations.map((location, index) => (
            <LocationRenderer key={index} location={location} />
          ))}
        </Columns>
        {editing && (
          <div>
            <br />
            <EditSiteDetails
              site={site}
              onFinishEditing={() => reloadSite() && setEditing(false)}
            />
          </div>
        )}
      </div>
      <div className='box'>
        <h2 className='subtitle'>Site Information</h2>
        <SiteInfoList
          siteId={site.id}
          info={site.info}
          reloadSite={reloadSite}
        />
      </div>
      <div className='box'>
        <h2 className='subtitle'>Organization Bindings</h2>
        <OrganizationBindingsList
          siteId={site.id}
          organizationBindings={site.organizationBindings}
          reloadSite={reloadSite}
        />
      </div>
    </div>
  )
}
